import {useLocation, useParams} from '@github-ui/react-router'
import {useAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useEffect, useRef, useState} from 'react'
import {isExplicitIssueQuery} from '../helpers/is-explicit-issue-query'
import {ensureDefaultSort, queryResolver} from '../helpers/query-resolver'
import {PULLS_PATH} from '../constants/routes'

interface UseListResultsParams {
  defaultFilterValue?: string
}

interface FetchResultsParams {
  q: string
  page: number
  sidebarLinkId?: string
}

type NavigationState = {sidebarLinkId?: string}

export function getListResultsLocation({
  q,
  page,
  defaultFilterValue,
  pathname,
}: {
  q: string
  page: number
  defaultFilterValue: string
  pathname: string
}) {
  const searchParams = new URLSearchParams()

  if (q !== defaultFilterValue) {
    searchParams.set('q', q)
  }

  if (page > 1) {
    searchParams.set('page', page.toString())
  }

  const search = searchParams.toString()
  return {pathname, search: search ? `?${search}` : ''}
}

/**
 * Hook to manage URL-based list results state.
 * Handles query parameters, pagination, and navigation for list results.
 *
 * @param params Configuration object
 * @param params.defaultFilterValue Default filter value to use when no query param is present
 * @returns An object containing:
 *   - fetchedQuery: The active query (empty string if explicitly cleared)
 *   - navigationQuery: The query resolved from the current route
 *   - currentPage: The current page number from URL
 *   - fetchResults: Function to navigate to new search results
 *   - locationSidebarLinkId: The sidebar link path
 */
export function useListResults({defaultFilterValue = ''}: UseListResultsParams) {
  const navigate = useNavigate()
  const location = useLocation()
  const {sendAnalyticsEvent} = useAnalytics()
  const navigationState = location.state as NavigationState | null

  const {mode} = useParams<{mode: string}>()

  const currentParams = new URLSearchParams(location.search)
  const [isEmptyQuery, setIsEmptyQuery] = useState(false)

  const query = queryResolver({searchParams: currentParams, params: {mode}})
  const fetchedQuery = isEmptyQuery ? '' : query

  const route = location.pathname + location.search
  const prevRouteRef = useRef(route)
  useEffect(() => {
    if (prevRouteRef.current !== route) {
      setIsEmptyQuery(false)
      prevRouteRef.current = route
    }
  }, [route])

  const currentPage = Number(currentParams.get('page') || 1)

  const fetchResults = useCallback(
    ({q, page, sidebarLinkId}: FetchResultsParams) => {
      if (isExplicitIssueQuery(q)) {
        sendAnalyticsEvent('pull-requests.search', 'IS_ISSUE', {query: q})

        navigate(`/issues?q=${encodeURIComponent(q)}`)
        return
      }

      sendAnalyticsEvent('pull-requests.search', 'LIST_RESULTS', {query: q})

      if (!q) {
        setIsEmptyQuery(true)
        return
      }

      const effectiveSidebarLinkId = sidebarLinkId !== undefined ? sidebarLinkId : navigationState?.sidebarLinkId
      const pathname = mode ? `${PULLS_PATH}/${mode}` : PULLS_PATH
      const destination = getListResultsLocation({q, page, defaultFilterValue, pathname})
      if (isEmptyQuery && ensureDefaultSort(q) === query) {
        const isCurrentRoute = destination.pathname === location.pathname && destination.search === location.search
        if (isCurrentRoute) {
          setIsEmptyQuery(false)
          if (effectiveSidebarLinkId === navigationState?.sidebarLinkId) return
        }
      }

      navigate(destination, effectiveSidebarLinkId ? {state: {sidebarLinkId: effectiveSidebarLinkId}} : undefined)
    },
    [
      defaultFilterValue,
      isEmptyQuery,
      location.pathname,
      location.search,
      mode,
      navigate,
      navigationState?.sidebarLinkId,
      query,
      sendAnalyticsEvent,
      setIsEmptyQuery,
    ],
  )

  return {
    fetchedQuery,
    navigationQuery: query,
    currentPage,
    fetchResults,
    mode,
    locationSidebarLinkId: navigationState?.sidebarLinkId ?? mode,
  }
}
